import * as React from 'react';
import {useState} from 'react';
import {graphql, useMutation} from 'react-relay';
import {Link, useNavigate} from 'react-router-dom';
import {ArrowLeft} from 'lucide-react';
import {OpsKitNewPageCreateMutation} from './__generated__/OpsKitNewPageCreateMutation.graphql';
import {ghostButton, inputClass, primaryButton} from './lib';

const CREATE = graphql`
    mutation OpsKitNewPageCreateMutation($input: CreateKitInput!) {
        createKit(input: $input) {
            kit { id assetTag }
            userErrors { message }
        }
    }`;

// Creates the KIT asset (tag assigned by the server), then hands off to the kit
// page where members are scanned in one at a time.
const OpsKitNewPage: React.FC = () => {
    const navigate = useNavigate();
    const [create, creating] = useMutation<OpsKitNewPageCreateMutation>(CREATE);
    const [form, setForm] = useState({description: '', dailyRate: '', notes: ''});
    const [errors, setErrors] = useState<string[]>([]);

    const submit = (e: React.FormEvent) => {
        e.preventDefault();
        const rate = form.dailyRate.trim() === '' ? null : Number(form.dailyRate);
        if (rate !== null && (Number.isNaN(rate) || rate < 0)) {
            setErrors(['Daily rate must be a number']);
            return;
        }
        create({
            variables: {input: {description: form.description.trim(), dailyRate: rate, notes: form.notes.trim() || null}},
            onCompleted: (res) => {
                const payload = res.createKit;
                if (payload?.kit) {
                    navigate(`/ops/kits/${encodeURIComponent(payload.kit.id)}`);
                } else {
                    setErrors(payload?.userErrors.map((u) => u.message) ?? ['Something went wrong']);
                }
            },
            onError: (err) => setErrors([err.message]),
        });
    };

    return (
        <div className="max-w-xl">
            <Link to="/ops/inventory" className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-white mb-4">
                <ArrowLeft className="w-3 h-3"/> Inventory
            </Link>
            <h1 className="text-3xl font-display font-bold mb-6">New kit</h1>
            <form onSubmit={submit} className="space-y-4">
                <label className="block">
                    <span className="block text-xs uppercase tracking-wider text-gray-500 mb-1">Description</span>
                    <input
                        value={form.description}
                        onChange={(e) => setForm({...form, description: e.target.value})}
                        placeholder="e.g. FX6 run & gun"
                        autoFocus
                        required
                        className={`${inputClass} w-full`}
                    />
                </label>
                <label className="block">
                    <span className="block text-xs uppercase tracking-wider text-gray-500 mb-1">Daily rate ($)</span>
                    <input
                        value={form.dailyRate}
                        onChange={(e) => setForm({...form, dailyRate: e.target.value})}
                        inputMode="decimal"
                        placeholder="Optional"
                        className={`${inputClass} w-40`}
                    />
                </label>
                <label className="block">
                    <span className="block text-xs uppercase tracking-wider text-gray-500 mb-1">Notes</span>
                    <textarea
                        value={form.notes}
                        onChange={(e) => setForm({...form, notes: e.target.value})}
                        rows={3}
                        className={`${inputClass} w-full`}
                    />
                </label>
                {errors.length > 0 && (
                    <ul className="text-sm text-red-400">{errors.map((m) => <li key={m}>{m}</li>)}</ul>
                )}
                <div className="flex gap-3">
                    <button type="submit" disabled={creating} className={primaryButton}>Create kit</button>
                    <Link to="/ops/inventory" className={ghostButton}>Cancel</Link>
                </div>
                <p className="text-xs text-gray-500">The kit gets the next LWM-KIT tag; you add members by scanning them on the next screen.</p>
            </form>
        </div>
    );
};

export default OpsKitNewPage;
